import React from 'react';
import * as LucideIcons from '@/lib/icons';
import { preventOrphans } from '@/lib/typography-utils';

interface IconCardProps {
  icon: string;
  title: string;
  children: React.ReactNode;
  className?: string;
  variant?: 'neutral' | 'primary' | 'purple' | 'teal' | 'emerald' | 'coral';
  size?: 'sm' | 'md' | 'lg';
  columnCount?: number;
  titleSizeClass?: string;
  bodySizeClass?: string;
  iconSizeClass?: string;
}

export const IconCard: React.FC<IconCardProps> = ({ 
  icon, 
  title, 
  children,
  className = '',
  variant = 'neutral',
  size = 'md',
  columnCount = 3,
  titleSizeClass,
  bodySizeClass,
  iconSizeClass
}) => {
  // Convert icon string to PascalCase to match Lucide component names
  const iconName = icon ? icon.split('-').map(word => 
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join('') : '';
  
  // Get the icon component from Lucide with fallback
  const IconComponent = iconName ? (LucideIcons as any)[iconName] : null;
  const FallbackIcon = LucideIcons.Zap;
  
  // Variant configurations with your brand colors and gradients
  const variants = {
    neutral: {
      background: 'bg-neutral-50',
      iconBg: 'bg-gradient-to-br from-primary/20 to-primary/10',
      shadow: 'shadow-elevation-1 hover:shadow-elevation-2',
      border: 'border-neutral-200',
      hover: 'hover:scale-[1.01]'
    },
    primary: {
      background: 'bg-white',
      iconBg: 'icon-gradient-primary',
      shadow: 'shadow-elevation-2 hover:shadow-elevation-3',
      border: 'border-primary/20',
      hover: 'hover:scale-[1.02] hover-glow-purple'
    },
    purple: {
      background: 'gradient-purple-light',
      iconBg: 'icon-gradient-purple',
      shadow: 'shadow-purple hover:shadow-elevation-3',
      border: 'border-purple-200',
      hover: 'hover:scale-[1.02] hover-glow-purple'
    },
    teal: {
      background: 'gradient-teal-light',
      iconBg: 'icon-gradient-teal',
      shadow: 'shadow-teal hover:shadow-elevation-3',
      border: 'border-cyan-200',
      hover: 'hover:scale-[1.02]'
    },
    emerald: {
      background: 'gradient-emerald-light',
      iconBg: 'icon-gradient-cyan',
      shadow: 'shadow-emerald hover:shadow-elevation-3',
      border: 'border-emerald-200',
      hover: 'hover:scale-[1.02]'
    },
    coral: {
      background: 'gradient-coral-light',
      iconBg: 'icon-gradient-coral',
      shadow: 'shadow-coral hover:shadow-elevation-3',
      border: 'border-orange-200',
      hover: 'hover:scale-[1.02]'
    }
  };
  
  // Get responsive sizing based on column count - following COMPONENT-VIEWPORT-MATRIX.md
  const getContainerPadding = () => {
    if (columnCount <= 2) return 'p-6 lg:p-8';
    if (columnCount === 3) return 'p-5 lg:p-6';
    return 'p-4 lg:p-5'; // 4+ columns
  };
  
  const getIconSize = () => {
    // Use passed class or determine from column count
    if (iconSizeClass) return iconSizeClass;
    
    if (columnCount <= 2) return 'w-14 h-14'; // 14×14 for 2-col
    if (columnCount === 3) return 'w-12 h-12'; // 12×12 for 3-col
    if (columnCount === 4) return 'w-10 h-10'; // 10×10 for 4-col
    return 'w-8 h-8'; // 8×8 for 6-col
  };
  
  const getIconInnerSize = () => {
    if (columnCount <= 2) return 'w-7 h-7';
    if (columnCount === 3) return 'w-6 h-6';
    if (columnCount === 4) return 'w-5 h-5';
    return 'w-4 h-4';
  };
  
  const getTitleSize = () => {
    // Use passed class or determine from column count
    if (titleSizeClass) return titleSizeClass;
    
    if (columnCount <= 2) return 'text-xl sm:text-2xl font-bold';
    if (columnCount === 3) return 'text-lg sm:text-xl font-bold';
    if (columnCount === 4) return 'text-base sm:text-lg font-bold';
    return 'text-sm font-bold'; // 5+ columns
  };
  
  const getBodySize = () => {
    // Use passed class or determine from column count
    if (bodySizeClass) return bodySizeClass;
    
    if (columnCount <= 2) return 'text-base';
    if (columnCount === 3) return 'text-sm sm:text-base';
    if (columnCount === 4) return 'text-xs sm:text-sm';
    return 'text-xs'; // 5+ columns
  };

  // Legacy size configurations for backwards compatibility
  const sizes = {
    sm: {
      container: 'p-5',
      icon: 'w-10 h-10',
      iconSize: 'w-5 h-5',
      title: 'text-lg font-semibold',
      content: 'text-sm'
    },
    md: {
      container: 'p-6',
      icon: 'w-12 h-12',
      iconSize: 'w-6 h-6',
      title: 'text-xl font-bold',
      content: 'text-base'
    },
    lg: {
      container: 'p-8',
      icon: 'w-14 h-14',
      iconSize: 'w-7 h-7',
      title: 'text-2xl font-bold',
      content: 'text-lg'
    }
  };
  
  const variantConfig = variants[variant] || variants.neutral;
  const sizeConfig = sizes[size] || sizes.md;
  
  // Use configuration-based sizing when columnCount is provided
  const useConfigBased = columnCount && (titleSizeClass || bodySizeClass || iconSizeClass || columnCount !== 3);
  
  return (
    <div className={`
      ${variantConfig.background}
      ${variantConfig.shadow}
      ${variantConfig.hover}
      border ${variantConfig.border}
      rounded-md ${useConfigBased ? getContainerPadding() : sizeConfig.container}
      transition-all duration-300 ease-out
      group relative overflow-hidden
      prose-safe min-w-0 w-full
      ${className}
    `} style={{ isolation: 'isolate' }}>
      {/* Subtle gradient overlay on hover */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
      
      {/* Content */}
      <div className="relative z-10">
        {/* Enhanced Icon */}
        <div className="mb-4">
          <div className={`
            ${variantConfig.iconBg}
            ${useConfigBased ? getIconSize() : sizeConfig.icon}
            rounded-xl flex items-center justify-center
            shadow-lg group-hover:shadow-xl
            transition-all duration-300
            group-hover:scale-110
          `}>
            {IconComponent ? (
              <IconComponent className={`${useConfigBased ? getIconInnerSize() : sizeConfig.iconSize} text-white drop-shadow-sm`} />
            ) : (
              <FallbackIcon className={`${useConfigBased ? getIconInnerSize() : sizeConfig.iconSize} text-white drop-shadow-sm`} />
            )}
          </div>
        </div>
        
        {/* Enhanced Title */}
        <h3 className={`
          ${useConfigBased ? getTitleSize() : 'component-title'}
          text-gray-900 mb-3
          group-hover:text-gray-800
          transition-colors duration-300
        `} style={{ textWrap: 'balance' }}>
          {preventOrphans(title)}
        </h3>
        
        {/* Enhanced Content with natural text flow */}
        <div className={`
          ${useConfigBased ? getBodySize() : 'component-body'}
          text-gray-700 leading-relaxed
          group-hover:text-gray-600
          transition-colors duration-300
        `} style={{ textWrap: 'pretty' }}>
          {typeof children === 'string' ? preventOrphans(children) : children}
        </div>
      </div>
      
      {/* Subtle shine effect */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-out" />
    </div>
  );
};